/**
 * What the paywall offers, and how a tap on it reaches Stripe.
 *
 * The amounts below are display copy only. The server holds the Stripe price
 * ids and picks one from the plan name, so the browser cannot name a price —
 * editing this file changes what a parent reads, never what they are charged.
 * Both still have to agree, so when a price moves in Stripe it moves here in
 * the same commit.
 */

export type PlanId = 'year' | 'month' | 'test';

export interface Plan {
  id: PlanId;
  name: string;
  /** The headline amount, e.g. `$119`. */
  price: string;
  /** The unit that follows it, small — `/yr`. */
  per: string;
  note: string;
  tag?: string;
}

/** Live Stripe prices, tax-exclusive: VAT is added at checkout where it applies. */
export const PLANS: Plan[] = [
  {
    id: 'year',
    name: 'Yearly',
    // The headline is the monthly equivalent, as on the monthly card, so the
    // two compare at a glance; the note says what is actually charged. $119 a
    // year is $9.92 a month, rounded to the $9.99 price point (Jernej,
    // 2026-09-07). Against $20 a month that is a saving of 50%.
    price: '$9.99',
    per: '/mo',
    note: 'Billed $119 yearly · save 50%',
    tag: 'Best value',
  },
  {
    id: 'month',
    name: 'Monthly',
    price: '$20',
    per: '/mo',
    note: 'Billed monthly · cancel anytime',
  },
];

/**
 * Open Stripe Checkout for a plan.
 *
 * Resolves only when something went wrong: on success the browser is already
 * on its way to Stripe, so there is nothing left for the caller to render.
 * Apple Pay and Google Pay appear on that page for the wallets the device
 * actually has — they are configured on the Stripe account, not asked for here.
 */
/** Which funnel is asking, so Stripe sends the parent back to the right one. */
export type CheckoutFrom = 'onboarding' | 'bible';

export async function startCheckout(plan: PlanId, from: CheckoutFrom = 'onboarding'): Promise<string | null> {
  try {
    const r = await fetch('/__checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan, from }),
    });
    const data = await r.json().catch(() => null);
    if (!r.ok || !data?.url) {
      return data?.error || `Checkout is unavailable right now (${r.status}).`;
    }
    // `assign`, so the Back button out of Checkout returns to the funnel — a
    // reload, but one that resumes on the paywall from the snapshot taken
    // just before leaving (see resume.ts). The redirect itself happens here
    // in the page, so there is no server redirect to bounce back through.
    location.assign(data.url as string);
    return null;
  } catch {
    return 'Could not reach the payment server. Check your connection and try again.';
  }
}

/**
 * The $1 test plan. Hidden until test mode is unlocked (seven taps on the
 * paywall preview); exists so a real card can be run through Checkout end to end. The
 * server maps it to STRIPE_PRICE_TEST, which must point at a recurring price.
 */
export const TEST_PLAN: Plan = {
  id: 'test',
  name: 'Test',
  price: '$1',
  per: '/mo',
  note: 'Internal test plan · real charge of $1',
};

/**
 * Call `cb` when the page comes back to life after the trip to Checkout.
 *
 * Pressing Back out of Stripe usually does NOT reload the funnel: the browser
 * restores it from the back/forward cache, live JavaScript state and all. So
 * the "opening secure checkout…" flag that was set just before `location.assign`
 * comes back set, and the button the parent needs is disabled with no way to
 * clear it short of a manual refresh.
 *
 * `pageshow` with `persisted` is that restore. `visibilitychange` covers the
 * browsers that hand the tab back without firing it; resetting once more than
 * necessary costs nothing, because all this ever does is re-enable a button.
 *
 * Returns the unsubscribe.
 */
export function onReturnFromCheckout(cb: () => void): () => void {
  const onShow = (e: PageTransitionEvent) => { if (e.persisted) cb(); };
  const onVisible = () => { if (document.visibilityState === 'visible') cb(); };
  window.addEventListener('pageshow', onShow);
  document.addEventListener('visibilitychange', onVisible);
  return () => {
    window.removeEventListener('pageshow', onShow);
    document.removeEventListener('visibilitychange', onVisible);
  };
}

/** How a parent got back here from Stripe, if they did. */
export type CheckoutReturn = 'success' | 'cancelled' | null;

export function checkoutReturn(): CheckoutReturn {
  const v = new URLSearchParams(location.search).get('checkout');
  return v === 'success' || v === 'cancelled' ? v : null;
}
